import { RSAA } from 'redux-api-middleware'
import { getApiPath, makeQueryParams } from 'sidecar-utility-library'
import { validateCoordinates } from 'utils/validation'
import types from './types'

export { default as types } from './types'

/** Initial state for suggestions store */
export const initialState = {
  suggestions: {
    specialtiesBusy: false,
    providersBusy: false,
    error: null,
    entities: {
      specialties: [],
      providers: [],
    },
  },
}

/**
 * Fetches search suggestions for the typeahead functionality based on the
 * provided query and location
 *
 * @param {Object} params - Search parameters
 * @param {string} params.query - The search query string
 * @param {number} params.lat - Latitude for location context
 * @param {number} params.lon - Longitude for location context
 * @returns {Object} Redux API middleware action
 */
/**
 * Fetches specialty suggestions for the typeahead functionality
 *
 * @param {Object} params - Search parameters
 * @param {string} params.query - The search query string
 * @returns {Object} Redux API middleware action
 */
export const fetchSpecialtySuggestions = ({ query }) => {
  // Build query parameters
  const queryParams = { query }

  // Add session ID if available
  if (window?.heap?.getSessionId) {
    queryParams.sessionId = window.heap.getSessionId() || ''
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'doc',
        `search/suggest/specialties${makeQueryParams(queryParams)}`,
        'v2',
      ),
      method: 'GET',
      types: [
        types.FETCH_SPECIALTY_SUGGESTIONS_REQUEST,
        types.FETCH_SPECIALTY_SUGGESTIONS_SUCCESS,
        types.FETCH_SPECIALTY_SUGGESTIONS_ERROR,
      ],
    },
  }
}

/**
 * Fetches provider suggestions for the typeahead functionality based on the
 * provided query and location
 *
 * @param {Object} params - Search parameters
 * @param {string} params.query - The search query string
 * @param {number} params.lat - Latitude for location context
 * @param {number} params.lon - Longitude for location context
 * @returns {Object} Redux API middleware action
 */
export const fetchProviderSuggestions = ({ query, lat, lon }) => {
  // Validate coordinates
  const coords = validateCoordinates(lat, lon)

  // Build query parameters
  const queryParams = { query }

  // Only add coordinates if they are valid
  if (coords) {
    queryParams.lat = coords.lat
    queryParams.lon = coords.lon
  }

  // Add session ID if available
  if (window?.heap?.getSessionId) {
    queryParams.sessionId = window.heap.getSessionId() || ''
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'doc',
        `search/suggest/providers${makeQueryParams(queryParams)}`,
        'v2',
      ),
      method: 'GET',
      types: [
        types.FETCH_PROVIDER_SUGGESTIONS_REQUEST,
        types.FETCH_PROVIDER_SUGGESTIONS_SUCCESS,
        types.FETCH_PROVIDER_SUGGESTIONS_ERROR,
      ],
    },
  }
}

/** Action to clear suggestions */
export const clearSuggestions = () => ({
  type: types.CLEAR_SUGGESTIONS,
})

/** Suggestions reducer */
export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    // Specialty suggestions handlers
    case types.FETCH_SPECIALTY_SUGGESTIONS_REQUEST:
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          specialtiesBusy: true,
          error: null,
        },
      }

    case types.FETCH_SPECIALTY_SUGGESTIONS_SUCCESS: {
      const specialties = payload?.specialties || []
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          specialtiesBusy: false,
          error: null,
          entities: {
            ...state.suggestions.entities,
            specialties,
          },
        },
      }
    }

    case types.FETCH_SPECIALTY_SUGGESTIONS_ERROR:
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          specialtiesBusy: false,
          error: payload?.message || 'Error fetching specialty suggestions',
          entities: {
            ...state.suggestions.entities,
            specialties: [],
          },
        },
      }

    // Provider suggestions handlers
    case types.FETCH_PROVIDER_SUGGESTIONS_REQUEST:
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          providersBusy: true,
          error: null,
        },
      }

    case types.FETCH_PROVIDER_SUGGESTIONS_SUCCESS: {
      const providers = payload?.providers || []
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          providersBusy: false,
          error: null,
          entities: {
            ...state.suggestions.entities,
            providers,
          },
        },
      }
    }

    case types.FETCH_PROVIDER_SUGGESTIONS_ERROR:
      return {
        ...state,
        suggestions: {
          ...state.suggestions,
          providersBusy: false,
          error: payload?.message || 'Error fetching provider suggestions',
          entities: {
            ...state.suggestions.entities,
            providers: [],
          },
        },
      }

    case types.CLEAR_SUGGESTIONS:
      return {
        ...state,
        suggestions: {
          ...initialState.suggestions,
        },
      }

    default:
      return state
  }
}
